// Typed models for the communication-template manager (SB-14943). Shared by the SysAdmin
// master-detail screen and the org-level Settings → Emails tab. camelCase over the wire.

/**
 * How a Reply-To is decided (SB-15933). Used both for the organisation's default and for a single
 * template's override.
 *
 * The numbers are the `ReplyToMode TINYINT` column values and are part of the schema — do not
 * renumber. A nullable address alone cannot tell "fall back to the organisation default" apart from
 * "this deliberately accepts no replies", which is why the mode is explicit.
 */
export enum ReplyToMode {
  /** Take the organisation's default. On the organisation itself this reads as "not set". */
  Inherit = 0,
  /** Use this row's own address and display name. The address is mandatory in this mode. */
  Custom = 1,
  /** Send a no-reply Reply-To, ignoring any organisation default. */
  NoReply = 2
}

/**
 * The organisation's Reply-To default (SB-15933), from `OrganisationEmailSettings`.
 *
 * `null` from the API means the organisation has never saved any — the normal starting state, and
 * equivalent to `ReplyToMode.Inherit`: emails carry no Reply-To header at all.
 */
export interface OrganisationEmailReplyToSettings {
  replyToMode: ReplyToMode;
  defaultReplyToEmailAddress?: string | null;
  defaultReplyToDisplayName?: string | null;
}

export interface CommunicationTemplateSummary {
  id: number;
  communicationTemplateItemId: number;
  templateName: string;
  emailSubject?: string;
  isUnlayerBased: boolean;
  /**
   * True when the organisation has customised the template: a person created its live version, or has
   * since edited it or switched it live (SB-15966). Only the type list carries it.
   */
  isCustomised?: boolean;
  isEnabled: boolean;
  sendToStudents: boolean;
  sendToParents: boolean;
  sendToStaff?: boolean;
  isOrganisationFooterIncluded: boolean;
  /** How this template's Reply-To is decided (SB-15933). */
  replyToMode: ReplyToMode;
  replyToEmailAddress?: string;
  replyToDisplayName?: string;
}

export interface CommunicationTemplate extends CommunicationTemplateSummary {
  /** User-assigned name for a user-created template (SB-14890); undefined for catalogue-type rows. */
  name?: string;
  emailBody?: string;
  pushNotification?: string;
  designJson?: string;
}

export interface SaveCommunicationTemplateRequest {
  communicationTemplateItemId: number;
  emailSubject?: string;
  emailBody?: string;
  pushNotification?: string;
  designJson?: string;
  isUnlayerBased: boolean;
  isEnabled: boolean;
  sendToStudents: boolean;
  sendToParents: boolean;
  sendToStaff?: boolean;
  isOrganisationFooterIncluded: boolean;
  /** How this template's Reply-To is decided (SB-15933). */
  replyToMode: ReplyToMode;
  replyToEmailAddress?: string;
  replyToDisplayName?: string;
}

/**
 * A user-created template (SB-14890) in the "My templates" list. No catalogue type; addressed by its
 * header `id`, carries a user-assigned `name` and optional `tags`.
 */
export interface CommunicationUserTemplateSummary {
  id: number;
  name: string;
  tags: string[];
  emailSubject?: string;
  isUnlayerBased: boolean;
  isEnabled: boolean;
  sendToStudents: boolean;
  sendToParents: boolean;
  sendToStaff: boolean;
  isOrganisationFooterIncluded: boolean;
  /** How this template's Reply-To is decided (SB-15933). */
  replyToMode: ReplyToMode;
  replyToEmailAddress?: string;
  replyToDisplayName?: string;
}

/** Create/update payload for a user-created template (SB-14890): name + content + flags + optional tags. */
export interface SaveUserTemplateRequest {
  name: string;
  emailSubject?: string;
  emailBody?: string;
  pushNotification?: string;
  designJson?: string;
  isUnlayerBased: boolean;
  isEnabled: boolean;
  sendToStudents: boolean;
  sendToParents: boolean;
  sendToStaff: boolean;
  isOrganisationFooterIncluded: boolean;
  /** How this template's Reply-To is decided (SB-15933). */
  replyToMode: ReplyToMode;
  replyToEmailAddress?: string;
  replyToDisplayName?: string;
  tags?: string[];
}

/** Duplicate request (SB-14890): clone a source template into a new user template in the caller's org. */
export interface DuplicateTemplateRequest {
  sourceTemplateId: number;
  /**
   * Source organisation; omit for the caller's own org, which is now the only permitted value. The
   * pre-built gallery used to live in organisation 1 and was exempt; SB-15889 moved it into the
   * curated library, which is not an organisation at all.
   */
  sourceOrganisationId?: number;
  newName?: string;
}

/**
 * A curated stock template in the "Pre-built" gallery (SB-15889).
 *
 * Not a user template: it belongs to no organisation, has no tags, no enabled flag and no Reply-To.
 * It is grouped and filtered in the gallery by `category`, and copied by `id` into a template of the
 * organisation's own - the copy is a snapshot with no link back.
 */
export interface CommunicationLibraryEntrySummary {
  id: number;
  name: string;
  description?: string;
  category?: string;
  communicationTemplateItemId: number;
  /** The template type's legacy title. Used for searching, not rendered on the card. */
  typeTitle?: string;
  emailSubject?: string;
  isUnlayerBased: boolean;
  /** Whether the entry carries push text - two of the stock entries consist of nothing else. */
  hasPushText: boolean;
  sortOrder: number;
}

/** Copies a stock library entry into a user template of the organisation's own (SB-15889). */
export interface CreateUserTemplateFromLibraryRequest {
  libraryId: number;
  name?: string;
}

/** Summary of a saved template version (SB-14943 versioning). */
export interface CommunicationTemplateVersionSummary {
  id: number;
  communicationTemplateId: number;
  versionNumber: number;
  name?: string;
  emailSubject?: string;
  isActive: boolean;
  isUnlayerBased: boolean;
  /** UTC creation instant, as sent. The column is a SQL datetime, so the string carries no zone. */
  createdDateUtc: string;
  /**
   * Organisation-local creation time, produced by `DateTimeInterceptor` from `createdDateUtc` (SB-15966) —
   * hence optional: it exists only on responses from the version routes the interceptor lists.
   */
  createdDate?: string;
  createdByPersonId?: number | null;
  /** Null when unknown: the first version of every template that predates versioning has no creator. */
  createdByPersonName?: string | null;
  /**
   * UTC instant of the last edit or later activation; null for a version never edited or switched live since
   * it was created (going live as it is created does not count).
   */
  modifiedDateUtc?: string | null;
  /** Organisation-local form of `modifiedDateUtc`, from the interceptor like `createdDate`. */
  modifiedDate?: string | null;
  modifiedByPersonId?: number | null;
  modifiedByPersonName?: string | null;
}

/** Full version payload (summary + editable content). */
export interface CommunicationTemplateVersion extends CommunicationTemplateVersionSummary {
  emailBody?: string;
  pushNotification?: string;
  designJson?: string;
}

/** Create/update payload for a version — content only (flags/reply-to live on the header). */
export interface SaveCommunicationTemplateVersionRequest {
  name?: string;
  emailSubject?: string;
  emailBody?: string;
  pushNotification?: string;
  designJson?: string;
  isUnlayerBased: boolean;

  /**
   * Header flags (SB-15889). The server applies them only when this request has to create the header,
   * i.e. on the first version of a type the organisation has never configured — the case where the
   * content comes from the stock library entry and its flags must travel with it. Omitted means "keep
   * the server's previous default", so callers that do not know about them are unaffected.
   */
  isEnabled?: boolean;
  sendToStudents?: boolean;
  sendToParents?: boolean;
  sendToStaff?: boolean;
  isOrganisationFooterIncluded?: boolean;
}

/** Preview render result: sanitised HTML + any merge tags the renderer will not substitute (SB-14895 WS4). */
export interface CommunicationTemplatePreviewResult {
  html: string;

  /**
   * Merge tags in the previewed HTML that the renderer does not recognise — they would reach the
   * recipient as literal `{TOKEN}` text (SB-14895).
   *
   * Currently always empty: both Preview endpoints hard-code an empty array while the server's
   * known-token set is too narrow to be trusted (it reflects one renderer's 53 constants and misses
   * everything Transport/Booking/Cca and the legacy placeholders resolve — see the `SB-15889` TODO on
   * `CommunicationTemplateController.Preview`). Optional because of that: the field is part of the
   * contract, not something a caller can rely on being populated. The client renders whatever arrives,
   * so switching the warning back on is a server-side change alone.
   */
  unrecognisedTags?: string[];
}

/** What a preview is of, beyond its HTML (SB-17220). */
export interface CommunicationTemplatePreviewOptions {
  /**
   * The HTML is a custom template's. It is shown inside the school's standard message, with the school's real
   * data and sample recipient and event values marked "(sample)", as its test email shows it.
   */
  isUserTemplate?: boolean;
  /** The custom template's subject, which the standard message shows as its heading. */
  subject?: string | null;
}

/**
 * Test-send request (SB-14894): a recipient address plus exactly one template selector — the
 * catalogue type (`communicationTemplateItemId`) or a user-created template (`userTemplateId`).
 * The saved active version is sent; the editor's unsaved HTML is not used.
 */
export interface TestSendRequest {
  toEmailAddress: string;
  communicationTemplateItemId?: number;
  userTemplateId?: number;
}

/** A single merge tag from the API; `value` is the legacy `~~{TOKEN}~~` form. */
export interface CommunicationMergeTag {
  group: string;
  name: string;
  value: string;
  /** Illustrative sample shown in the editor's in-canvas preview (cosmetic; real substitution is backend-side). */
  sample?: string;
}

/**
 * Unlayer's nested merge-tag catalogue shape (`options.mergeTags`). A group is an entry with a `name`
 * and a `mergeTags` child map; a leaf tag carries `value` (+ optional `sample`). Keyed by a slug so the
 * editor renders grouped sub-menus rather than a flat list.
 */
export interface UnlayerMergeTag {
  name: string;
  value?: string;
  sample?: string;
  mergeTags?: UnlayerMergeTags;
}

export interface UnlayerMergeTags {
  [key: string]: UnlayerMergeTag;
}

/** A selectable template type (for the "create new" flow): enum id + display name. */
export interface CommunicationTemplateType {
  id: number;
  name: string;
}

/** An organisation in the left-hand selector (from the legacy `WebOrganisation/GetOrganisations`). */
export interface OrganisationListItem {
  organisationId: number;
  title: string;
  /** Brand colours (hex, no leading '#') for the editor colour-picker presets (SB-14896 B1). */
  darkColour?: string;
  lightColour?: string;
}

/**
 * The organisation's own email footer (SB-14896) — an HTML *fragment* appended below the body of
 * every template whose "include email footer" switch is on. Not an Unlayer design: there is no
 * design JSON for it, and a document-shaped value would let its author move the mandatory
 * compliance block, so it stays a fragment.
 */
export interface OrganisationEmailFooter {
  organisationId: number;
  htmlFooterTemplate: string | null;
}

/**
 * The compliance block appended *below* the organisation's footer on every email (SB-14896).
 * Read-only: the organisation cannot edit, restyle, translate or switch it off.
 */
export interface EmailFooterCompliance {
  /**
   * Whether the block is actually emitted in this environment. It is produced only by the v2
   * Functions send lane, so where that flag is off emails carry the organisation footer and
   * nothing else — no postal address and no unsubscribe link. The panel must say so rather than
   * assert something untrue about legal compliance.
   */
  isActive: boolean;
  organisationName: string | null;
  /** Rendered CAN-SPAM sender + postal-address HTML; empty when none could be formed. */
  addressHtml: string | null;
  /** False is common, not exceptional — many organisations have no contact row at all. */
  hasPostalAddress: boolean;
  /** The unsubscribe line, rendered from the same literals the send path uses. Sample link only. */
  unsubscribeHtml: string | null;
  /**
   * The absolute public URL of the organisation's logo, for the footer's "Insert logo" and "Set to
   * default" buttons (SB-13366). `null`: the server answered and the school has no logo. Absent: a server
   * from before the field existed, which says nothing either way.
   */
  logoUrl?: string | null;
}
